import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { IPImage } from 'baseComponents';
import { useEffect, useState } from 'react';
import { darkThemeV9, lightThemeV9 } from 'themes/runtime/globalThemes-v9';

// the logo of the splash, the same size as in the static shell of index.html
const splashLogoSize = 72;

// how long the splash fades out (tokens.durationGentle)
const splashFadeMs = 250;

const useStyles = makeStyles({
  root: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationGentle,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  fading: {
    opacity: 0,
    pointerEvents: 'none',
  },
  logo: {
    width: `${splashLogoSize}px`,
    height: `${splashLogoSize}px`,
    animationName: {
      '0%': { transform: 'scale(1)', opacity: 0.85 },
      '50%': { transform: 'scale(1.04)', opacity: 1 },
      '100%': { transform: 'scale(1)', opacity: 0.85 },
    },
    animationDuration: '1.6s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  },
});

interface IAppSplashProps {
  // shown; when it turns false the splash fades out and goes
  visible: boolean;
  dark: boolean;
}

//
// The splash of a cold load (docs/phase-29-groups-consent-first-load.md, D29-2): the logo on the background of the
// theme, the same as the static shell of index.html, over the app until the first sign-in check has an answer, then
// faded out. It carries no text, so it reads the same before and after the translations load.
//
const AppSplash = (props: IAppSplashProps) => {
  const styles = useStyles();
  const [isGone, setIsGone] = useState<boolean>(!props.visible);

  useEffect(() => {
    if (props.visible) {
      setIsGone(false);

      return;
    }

    const timer = setTimeout(() => setIsGone(true), splashFadeMs);

    return () => clearTimeout(timer);
  }, [props.visible]);

  if (isGone) return null;

  return (
    <div
      className={mergeClasses(styles.root, !props.visible && styles.fading)}
      style={{ background: (props.dark ? darkThemeV9 : lightThemeV9).colorNeutralBackground1 }}
    >
      <IPImage src="/images/logo.svg" alt="" className={styles.logo} />
    </div>
  );
};

export default AppSplash;
